// Keep this file free of heavy imports: index.ts runs it on every page load to
// decide whether the Contextual SDK should be fetched at all.
import type { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";

export const CONTEXTUAL_THEME_SCRIPT_ID = "redo-contextual-theme-script";

/** Matches any Contextual SDK script, including a legacy Shopify ScriptTag. */
const CONTEXTUAL_THEME_SCRIPT_SRC_FRAGMENT = "/worker-sdk/theme-script";

export interface ContextualLoaderConfig {
  scriptUrl: string;
  fallbackScriptUrl?: string;
}

export function getContextualLoaderConfig(
  autocheck: Pick<WidgetAutocheck, "contextual">,
): ContextualLoaderConfig | undefined {
  const contextual = autocheck.contextual;
  if (!contextual?.enabled || !contextual.scriptUrl) {
    return undefined;
  }
  return {
    scriptUrl: contextual.scriptUrl,
    fallbackScriptUrl:
      contextual.fallbackScriptUrl &&
      contextual.fallbackScriptUrl !== contextual.scriptUrl
        ? contextual.fallbackScriptUrl
        : undefined,
  };
}

function findExistingContextualScript(doc: Document): HTMLScriptElement | null {
  return doc.querySelector<HTMLScriptElement>(
    `script[src*="${CONTEXTUAL_THEME_SCRIPT_SRC_FRAGMENT}"]`,
  );
}

export type ContextualLoadResult =
  | { status: "already-present"; src: string }
  | { status: "loaded"; src: string }
  | { status: "failed"; attempted: string[] };

/**
 * Injects the Contextual SDK as its own async script element. Nothing else in
 * the embed awaits it, so an ad blocker or outage on getcontextual.io cannot
 * hold up any other Redo product. On a load error the shared endpoint is tried
 * once, which the ScriptTag install could never do: a ctx subdomain whose CNAME
 * lapsed used to mean no SDK at all.
 */
export function loadContextualThemeScript(
  config: ContextualLoaderConfig,
  doc: Document = document,
): Promise<ContextualLoadResult> {
  const existing = findExistingContextualScript(doc);
  if (existing) {
    return Promise.resolve({ status: "already-present", src: existing.src });
  }

  const attempted: string[] = [];
  const inject = (src: string): Promise<boolean> =>
    new Promise((resolve) => {
      attempted.push(src);
      const script = doc.createElement("script");
      script.id = CONTEXTUAL_THEME_SCRIPT_ID;
      script.async = true;
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => {
        script.remove();
        resolve(false);
      };
      doc.head.append(script);
    });

  return (async () => {
    if (await inject(config.scriptUrl)) {
      return { status: "loaded", src: config.scriptUrl };
    }
    if (config.fallbackScriptUrl && (await inject(config.fallbackScriptUrl))) {
      return { status: "loaded", src: config.fallbackScriptUrl };
    }
    return { status: "failed", attempted };
  })();
}
